import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Lock,
  Unlock,
  Search,
  RefreshCw,
  Trash2,
  Key,
  CheckCircle,
  XCircle,
  User,
  Bot,
  Settings,
  BarChart3,
  Target,
  List,
  DollarSign,
  ShieldCheck,
  Code,
  BookOpen,
  Shield,
  TrendingUp,
  AlertCircle,
  Download,
  FileText,
  Building2,
  PackageCheck,
  Store,
  Database,
  ShieldAlert
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../components/AccessDenied';
import AIProviders from '../components/Admin/AIProviders';
import SSOConfiguration from '../components/Admin/SSOConfiguration';
import RoleManagement from '../components/Admin/RoleManagement';
import APIKeysConfig from '../components/Admin/APIKeysConfig';
import ProcessMetadata from '../components/Admin/ProcessMetadata';
import DeletedProjectsAdmin from '../components/Admin/DeletedProjectsAdmin';
import RiskAppetiteConfig from '../components/RCSA/RiskAppetiteConfig';
import RiskTaxonomyManagement from '../components/Admin/RiskTaxonomyManagement';
import ROIDashboard from '../components/Admin/ROIDashboard';
import AuditDashboard from './AuditDashboard';
import CodeStatistics from '../components/Admin/CodeStatistics';
import WorkflowToolsAdmin from '../components/Admin/WorkflowToolsAdmin';
import Pagination from '../components/Common/Pagination';
import UserCreateModal from '../components/Admin/UserCreateModal';
import LLMSelector from '../components/LLMSelector';
import TerminologyTemplateUpload from '../components/Terminology/TerminologyTemplateUpload';
import { FrameworkList, FrameworkStandard } from '../components/Framework/FrameworkList';
import { FrameworkDetailModal } from '../components/Framework/FrameworkDetailModal';
import { useProject } from '../contexts/ProjectContext';
import TenantManagement from './TenantManagement';
import SecurityScanDashboard from '../components/Admin/SecurityScanDashboard';
import EvidenceHarvesting from './EvidenceHarvesting';
import VendorManagement from './VendorManagement';
import AssetInventory from './AssetInventory';
import VulnerabilityDashboard from './VulnerabilityDashboard';
import VulnerabilityDetail from './VulnerabilityDetail';
import IpWhitelistManagement from '../components/Admin/IpWhitelistManagement';
import PrivilegedAccessReports from '../components/Admin/PrivilegedAccessReports';
import api from '../api';
import toast from 'react-hot-toast';

interface UserData {
  id: number;
  email: string;
  name: string;
  role: 'USER' | 'ADMIN' | 'MASTER' | 'SUPER_ADMIN';
  is_active: boolean;
  last_login: string | null;
  login_attempts: number;
  accountStatus: 'ACTIVE' | 'INACTIVE' | 'LOCKED';
  isLocked: boolean;
  mfa_enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  lockedUsers: number;
  roleDistribution: {
    USER: number;
    ADMIN: number;
    MASTER: number;
  };
  recentLogins: {
    id: number;
    email: string;
    name: string;
    lastLogin: string;
  }[];
}

const Admin: React.FC = () => {
  const { user: currentUser, loading: authLoading } = useAuth();
  const { currentProject } = useProject();
  const [users, setUsers] = useState<UserData[]>([]);
  const [activeTab, setActiveTab] = useState<'users-roles' | 'tenant-management' | 'security-code' | 'audit-logs' | 'risk-settings' | 'workflow-tools' | 'roi-dashboard'>('users-roles');
  const [usersRolesSubTab, setUsersRolesSubTab] = useState<'users' | 'roles'>('users');
  const [securityCodeSubTab, setSecurityCodeSubTab] = useState<'sso-logins' | 'ai-config' | 'code-statistics' | 'security-scan' | 'evidence-collection' | 'vendor-management' | 'asset-inventory' | 'vulnerability-management' | 'ip-whitelist'>('sso-logins');
  const [selectedVulnerabilityId, setSelectedVulnerabilityId] = useState<number | null>(null);
  const [aiConfigSubTab, setAiConfigSubTab] = useState<'providers' | 'api-keys' | 'llm-selector'>('providers');
  const [auditLogsSubTab, setAuditLogsSubTab] = useState<'audit-dashboard' | 'deleted-projects' | 'privileged-access'>('audit-dashboard');
  const [riskSettingsSubTab, setRiskSettingsSubTab] = useState<'process-metadata' | 'definitions-acronyms' | 'risk-appetite' | 'risk-taxonomy' | 'regulatory-frameworks'>('process-metadata');
  const [workflowToolsSubTab, setWorkflowToolsSubTab] = useState<'workflow-automation' | 'sop-formats'>('workflow-automation');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const itemsPerPage = 10;
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Framework-specific state
  const [frameworks, setFrameworks] = useState<FrameworkStandard[]>([]);
  const [frameworkLoading, setFrameworkLoading] = useState(false);
  const [frameworkError, setFrameworkError] = useState<string | null>(null);
  const [selectedFramework, setSelectedFramework] = useState<FrameworkStandard | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [frameworkStats, setFrameworkStats] = useState({
    totalFrameworks: 0,
    totalStandards: 0,
    frameworkTypes: 0
  });


  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { isActive: statusFilter })
      });

      const response = await api.get(`/api/admin/users?${params}`);

      const data = response.data;
      setUsers(data.users || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalUsers(data.pagination?.totalRecords || 0);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, roleFilter, statusFilter]);

  // Fetch user statistics
  const fetchStats = async () => {
    try {
      const response = await api.get('/api/admin/statistics');
      setStats(response.data);
    } catch (error: any) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Load frameworks
  const loadFrameworks = async () => {
    try {
      setFrameworkLoading(true);
      setFrameworkError(null);
      const response = await api.get('/api/framework-standards', {
        params: { limit: 1000 }
      });

      if (response.data.success) {
        setFrameworks(response.data.data);
      }
    } catch (err: any) {
      console.error('Error loading frameworks:', err);
      setFrameworkError(err.response?.data?.message || 'Failed to load frameworks');
    } finally {
      setFrameworkLoading(false);
    }
  };

  const loadFrameworkStats = async () => {
    try {
      const response = await api.get('/api/framework-standards/frameworks');
      if (response.data.success) {
        const frameworkData = response.data.data;
        const totalStandards = frameworkData.reduce((sum: number, fw: any) => sum + parseInt(fw.standard_count), 0);

        setFrameworkStats({
          totalFrameworks: frameworkData.length,
          totalStandards: totalStandards,
          frameworkTypes: frameworkData.length
        });
      }
    } catch (err) {
      console.error('Error loading framework stats:', err);
    }
  };

  const handleSelectFramework = (framework: FrameworkStandard) => {
    setSelectedFramework(framework);
    setShowDetailModal(true);
  };

  const handleLoadControls = async (frameworkId: number) => {
    try {
      const response = await api.get('/api/control-standards', {
        params: { framework_standard_id: frameworkId }
      });

      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (err) {
      console.error('Error loading controls:', err);
      return [];
    }
  };

  const handleLoadCitations = async (frameworkId: number) => {
    try {
      const response = await api.get('/api/compliance-citations', {
        params: {
          framework_standard_id: frameworkId,
          limit: 100
        }
      });

      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (err) {
      console.error('Error loading citations:', err);
      return [];
    }
  };

  const handleExportFrameworks = () => {
    const headers = ['Framework', 'Standard ID', 'Standard Name', 'Category', 'Description'];
    const rows = frameworks.map(fw => [
      fw.framework_name,
      fw.standard_id,
      fw.standard_name,
      fw.category || '',
      fw.description || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `framework-standards-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Frameworks exported successfully');
  };

  // Read tab and framework from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const frameworkParam = urlParams.get('framework');

    if (tabParam && ['users-roles', 'ai-config', 'sso-config', 'workflow-tools', 'process-metadata', 'system', 'deleted-projects', 'risk-appetite', 'risk-taxonomy', 'roi-dashboard', 'audit-dashboard', 'code-statistics', 'regulatory-frameworks', 'templates', 'vulnerability-management'].includes(tabParam)) {
      setActiveTab(tabParam as any);
      if (tabParam === 'vulnerability-management') {
        setActiveTab('security-code');
        setSecurityCodeSubTab('vulnerability-management');
      }
    }

    // If framework parameter is provided, we'll handle it after frameworks are loaded
    if (frameworkParam && tabParam === 'regulatory-frameworks') {
      // Store it temporarily to use after frameworks load
      sessionStorage.setItem('pendingFrameworkHighlight', frameworkParam);
    }
  }, []);

  // Handle framework highlighting from URL after frameworks are loaded
  useEffect(() => {
    if (activeTab === 'risk-settings' && riskSettingsSubTab === 'regulatory-frameworks' && frameworks.length > 0) {
      const pendingFramework = sessionStorage.getItem('pendingFrameworkHighlight');
      if (pendingFramework) {
        // Find the framework that matches the name
        const framework = frameworks.find(f =>
          f.framework_name.toLowerCase() === pendingFramework.toLowerCase() ||
          f.standard_id.toLowerCase().includes(pendingFramework.toLowerCase())
        );

        if (framework) {
          // Auto-open the detail modal for this framework
          setSelectedFramework(framework);
          setShowDetailModal(true);
        }

        // Clear the pending highlight
        sessionStorage.removeItem('pendingFrameworkHighlight');
      }
    }
  }, [activeTab, riskSettingsSubTab, frameworks]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers]);

  useEffect(() => {
    if (activeTab === 'risk-settings' && riskSettingsSubTab === 'regulatory-frameworks') {
      loadFrameworks();
      loadFrameworkStats();
    }
  }, [activeTab, riskSettingsSubTab]);


  const handleUserAction = async (userId: number, action: string, data?: any) => {
    try {
      setActionLoading(userId);
      let response;

      switch (action) {
        case 'toggle-status':
          response = await api.post(`/api/admin/users/${userId}/toggle-status`);
          break;
        case 'unlock':
          response = await api.post(`/api/admin/users/${userId}/unlock`);
          break;
        case 'reset-password':
          response = await api.post(`/api/admin/users/${userId}/reset-password`, { generateRandom: true });
          break;
        case 'delete':
          // ✅ ENHANCED CONFIRMATION DIALOG
          const user = users.find(u => u.id === userId);
          const confirmMessage = `Are you sure you want to delete user "${user?.name || 'Unknown'}" (${user?.email || 'Unknown'})?
          This action cannot be undone and will:
          - Permanently remove the user account
          - Remove access to all projects and documents
          - Clear all associated permissions

          Type "DELETE" to confirm:`;

          const confirmation = prompt(confirmMessage);
          if (confirmation !== 'DELETE') {
            return;
          }

          if (user?.role === 'SUPER_ADMIN') {
            toast.error('Cannot delete Super Admin accounts');
            return;
          }

          response = await api.delete(`/api/admin/users/${userId}`);
          break;
        default:
          return;
      }

      const result = response.data;

      if (action === 'reset-password' && result.temporaryPassword) {
        const tempPasswordAlert = `Password reset successfully!

          Temporary password: ${result.temporaryPassword}

          Please share this with the user securely and ask them to change it on first login.`;

        // Copy to clipboard if possible
        if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(result.temporaryPassword);
            toast.success('Password reset! Temporary password copied to clipboard.');
          } catch {
            alert(tempPasswordAlert);
          }
        } else {
          alert(tempPasswordAlert);
        }
      } else if (action === 'delete') {
        toast.success(`User "${result.deletedUser?.name || 'Unknown'}" has been deleted successfully`);
      } else {
        toast.success('Action completed successfully');
      }

      // Refresh data
      fetchUsers();
      fetchStats();

    } catch (error: any) {
      console.error('Error performing action:', error);

      // ✅ ENHANCED ERROR HANDLING
      const errorData = error.response?.data;
      let errorMessage = 'An unexpected error occurred';

      if (errorData) {
        if (errorData.error && errorData.message) {
          errorMessage = `${errorData.error}: ${errorData.message}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }

        if (errorData.projectCount || errorData.documentCount) {
          const details = [];
          if (errorData.projectCount) details.push(`${errorData.projectCount} projects`);
          if (errorData.documentCount) details.push(`${errorData.documentCount} documents`);

          errorMessage += `\n\nUser has: ${details.join(', ')}`;

          const offerTransfer = confirm(`${errorMessage}\n\nWould you like to transfer these items to another user first?`);
          if (offerTransfer) {
            return;
          }
        }
      }

      toast.error(errorMessage);

      // ✅ SPECIFIC ACTION FOR AUTH ERRORS
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('You do not have permission to perform this action');

        // Refresh user info in case role changed
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'bg-red-100 text-red-800';
      case 'ADMIN': return 'bg-blue-100 text-blue-800';
      case 'CONSULTANT': return 'bg-green-100 text-green-800';
      case 'READ_ONLY': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (user: UserData) => {
    if (!user.is_active) {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    if (user.isLocked) {
      return <Lock className="w-4 h-4 text-yellow-500" />;
    }
    return <CheckCircle className="w-4 h-4 text-green-500" />;
  };

  const tabs = [
    { id: 'users-roles', label: 'Users & Roles', icon: Users },
    ...(currentUser?.role === 'SUPER_ADMIN' ? [{ id: 'tenant-management', label: 'Tenant Management', icon: Building2 }] : []),
    { id: 'security-code', label: 'Security/Code', icon: Shield },
    { id: 'audit-logs', label: 'Audit/Logs', icon: ShieldCheck },
    { id: 'risk-settings', label: 'Risk Settings', icon: Target },
    { id: 'workflow-tools', label: 'Workflow Tools', icon: TrendingUp },
    { id: 'roi-dashboard', label: 'ROI', icon: DollarSign }
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  // Security check - only SUPER_ADMIN users can access admin
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!currentUser || (currentUser.role !== 'SUPER_ADMIN' && currentUser.role !== 'ADMIN')) {
    return <AccessDenied requiredRole="ADMIN" message={`Admin dashboard access is restricted to Admin and Super Admin users only. Current user: ${currentUser?.email}, Role: ${currentUser?.role}`} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Comprehensive system administration and user management</p>
              <div className="flex items-center mt-2 space-x-4">
                <div className="flex items-center text-sm text-gray-500">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  System Status: Online
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Users className="w-4 h-4 mr-1" />
                  {stats ? `${stats.totalUsers} Total Users` : 'Loading...'}
                </div>
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:shadow-lg transition-shadow"
            >
              <UserPlus className="w-4 h-4" />
              <span>Create User</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <nav className="flex space-x-2 p-2 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'users-roles' && (
        <div className="space-y-6">
          {/* Sub-tabs for Users & Roles */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex space-x-2">
              <button
                onClick={() => setUsersRolesSubTab('users')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  usersRolesSubTab === 'users'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>User Management</span>
              </button>
              <button
                onClick={() => setUsersRolesSubTab('roles')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  usersRolesSubTab === 'roles'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span>Roles & Permissions</span>
              </button>
            </div>
          </div>

          {usersRolesSubTab === 'users' && (
            <>
              {/* Statistics Cards */}
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-6 rounded-lg shadow border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Total Users</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
                      </div>
                      <Users className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Active Users</p>
                        <p className="text-2xl font-bold text-green-600">{stats.activeUsers}</p>
                      </div>
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Inactive Users</p>
                        <p className="text-2xl font-bold text-gray-600">{stats.inactiveUsers}</p>
                      </div>
                      <XCircle className="w-8 h-8 text-gray-600" />
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Locked Users</p>
                        <p className="text-2xl font-bold text-yellow-600">{stats.lockedUsers}</p>
                      </div>
                      <Lock className="w-8 h-8 text-yellow-600" />
                    </div>
                  </div>
                </div>
              )}

          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="SUPER_ADMIN">Super Admin</option>
                <option value="ADMIN">Admin</option>
                <option value="CONSULTANT">Consultant</option>
                <option value="READ_ONLY">Read Only</option>
              </select>
              <select
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setStatusFilter('');
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border rounded-md hover:bg-gray-50 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Clear Filters
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      MFA
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4">
                        <div className="flex items-center justify-center h-32">
                          <RefreshCw className="w-8 h-8 animate-spin text-electric-blue" />
                        </div>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <User className="w-5 h-5 text-gray-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(user)}
                            <span className="text-sm text-gray-900">
                              {user.accountStatus}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {/* MFA Status with enforcement indicator for privileged accounts */}
                          {user.mfa_enabled ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              <Shield className="w-3 h-3 mr-1" />
                              Enabled
                            </span>
                          ) : (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Required
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                              Not Set
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(user.last_login)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            {user.isLocked && (
                              <button
                                onClick={() => handleUserAction(user.id, 'unlock')}
                                disabled={actionLoading === user.id}
                                className="text-yellow-600 hover:text-yellow-800 disabled:opacity-50"
                                title="Unlock Account"
                              >
                                <Unlock className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleUserAction(user.id, 'toggle-status')}
                              disabled={actionLoading === user.id}
                              className={`${user.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'} disabled:opacity-50`}
                              title={user.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleUserAction(user.id, 'reset-password')}
                              disabled={actionLoading === user.id}
                              className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              title="Reset Password"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            {user.role !== 'SUPER_ADMIN' && (
                              <button
                                onClick={() => handleUserAction(user.id, 'delete')}
                                disabled={actionLoading === user.id}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                title="Delete User"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalUsers > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalUsers}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                itemName="users"
              />
            )}
          </div>
            </>
          )}

          {usersRolesSubTab === 'roles' && <RoleManagement />}
        </div>
      )}

      {/* Security/Code Tab */}
      {activeTab === 'security-code' && (
        <div className="space-y-6">
          {/* Sub-tabs for Security/Code */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setSecurityCodeSubTab('sso-logins')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'sso-logins'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span>SSO & Logins</span>
              </button>
              <button
                onClick={() => setSecurityCodeSubTab('ai-config')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'ai-config'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Bot className="w-4 h-4" />
                <span>AI Configuration</span>
              </button>
              <button
                onClick={() => setSecurityCodeSubTab('security-scan')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'security-scan'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Security Scan</span>
              </button>
              <button
                onClick={() => setSecurityCodeSubTab('code-statistics')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'code-statistics'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Code className="w-4 h-4" />
                <span>Code Statistics</span>
              </button>
              <button
                onClick={() => setSecurityCodeSubTab('evidence-collection')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'evidence-collection'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <PackageCheck className="w-4 h-4" />
                <span>Evidence Collection</span>
              </button>
              <button
                onClick={() => setSecurityCodeSubTab('vendor-management')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'vendor-management'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Store className="w-4 h-4" />
                <span>Vendor Management</span>
              </button>
              <button
                onClick={() => setSecurityCodeSubTab('asset-inventory')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'asset-inventory'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Database className="w-4 h-4" />
                <span>Asset Inventory</span>
              </button>
              <button
                onClick={() => {
                  setSecurityCodeSubTab('vulnerability-management');
                  setSelectedVulnerabilityId(null);
                }}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'vulnerability-management'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Vulnerability Management</span>
              </button>
              <button
                onClick={() => setSecurityCodeSubTab('ip-whitelist')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  securityCodeSubTab === 'ip-whitelist'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span>IP Whitelist</span>
              </button>
            </div>
          </div>

          {/* SSO & Logins Content */}
          {securityCodeSubTab === 'sso-logins' && <SSOConfiguration />}

          {/* AI Configuration Content */}
          {securityCodeSubTab === 'ai-config' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setAiConfigSubTab('providers')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      aiConfigSubTab === 'providers'
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Bot className="w-4 h-4" />
                    <span>AI Providers</span>
                  </button>
                  <button
                    onClick={() => setAiConfigSubTab('api-keys')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      aiConfigSubTab === 'api-keys'
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Key className="w-4 h-4" />
                    <span>API Keys</span>
                  </button>
                  <button
                    onClick={() => setAiConfigSubTab('llm-selector')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      aiConfigSubTab === 'llm-selector'
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Settings className="w-4 h-4" />
                    <span>LLM Model Selection</span>
                  </button>
                </div>
              </div>

              {aiConfigSubTab === 'providers' && <AIProviders />}
              {aiConfigSubTab === 'api-keys' && <APIKeysConfig />}
              {aiConfigSubTab === 'llm-selector' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">LLM Model Selection</h2>
                    <p className="text-gray-600">
                      Configure the default AI model used for analysis and processing throughout the application.
                      Only Super Admins can change this setting, which affects all users in the organization.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Current AI Model Configuration</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          Select the LLM model that best fits your organization's needs. Different models offer varying
                          levels of performance, speed, and capabilities.
                        </p>

                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <LLMSelector
                            showStatus={true}
                            onModelChange={(_modelId, modelName) => {
                              toast.success(`AI Model changed to ${modelName}`);
                            }}
                          />
                        </div>

                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                              <h4 className="font-semibold text-blue-900 text-sm mb-1">Model Selection Guidelines</h4>
                              <ul className="text-xs text-blue-700 space-y-1">
                                <li>• <strong>Claude 4 Sonnet:</strong> Best overall performance for complex analysis and document processing</li>
                                <li>• <strong>Claude 3.5 Haiku:</strong> Fastest response times for quick queries and simple tasks</li>
                                <li>• <strong>Claude 3 Opus:</strong> Most capable model for advanced reasoning and complex scenarios</li>
                                <li>• <strong>GPT/Gemini models:</strong> Alternative providers (requires API key configuration)</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <ShieldCheck className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-yellow-900 text-sm mb-1">Important Notes</h4>
                        <ul className="text-xs text-yellow-700 space-y-1">
                          <li>• Changes to the AI model affect all users across the organization</li>
                          <li>• Ensure API keys are configured before selecting non-Anthropic models</li>
                          <li>• Model availability depends on your configured API providers</li>
                          <li>• Different models may have different usage costs and rate limits</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Security Scan Content */}
          {securityCodeSubTab === 'security-scan' && <SecurityScanDashboard />}

          {/* Evidence Collection Content */}
          {securityCodeSubTab === 'evidence-collection' && <EvidenceHarvesting />}

          {/* Vendor Management Content */}
          {securityCodeSubTab === 'vendor-management' && <VendorManagement />}

          {/* Asset Inventory Content */}
          {securityCodeSubTab === 'asset-inventory' && <AssetInventory />}

          {/* Code Statistics Content */}
          {securityCodeSubTab === 'code-statistics' && <CodeStatistics />}

          {/* Vulnerability Management Content */}
          {securityCodeSubTab === 'vulnerability-management' && (
            selectedVulnerabilityId ? (
              <VulnerabilityDetail
                vulnerabilityId={selectedVulnerabilityId}
                onBack={() => setSelectedVulnerabilityId(null)}
              />
            ) : (
              <VulnerabilityDashboard
                onViewVulnerability={(id: number) => setSelectedVulnerabilityId(id)}
              />
            )
          )}

          {/* IP Whitelist Content */}
          {securityCodeSubTab === 'ip-whitelist' && <IpWhitelistManagement />}
        </div>
      )}

      {/* Audit/Logs Tab */}
      {activeTab === 'audit-logs' && (
        <div className="space-y-6">
          {/* Sub-tabs for Audit/Logs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex space-x-2">
              <button
                onClick={() => setAuditLogsSubTab('audit-dashboard')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  auditLogsSubTab === 'audit-dashboard'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Audit Dashboard</span>
              </button>
              <button
                onClick={() => setAuditLogsSubTab('deleted-projects')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  auditLogsSubTab === 'deleted-projects'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Trash2 className="w-4 h-4" />
                <span>Deleted Projects</span>
              </button>
              <button
                onClick={() => setAuditLogsSubTab('privileged-access')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  auditLogsSubTab === 'privileged-access'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span>Privileged Access</span>
              </button>
            </div>
          </div>

          {auditLogsSubTab === 'audit-dashboard' && <AuditDashboard />}
          {auditLogsSubTab === 'deleted-projects' && <DeletedProjectsAdmin />}
          {auditLogsSubTab === 'privileged-access' && <PrivilegedAccessReports />}
        </div>
      )}

      {/* ROI Dashboard - Moved to end */}
      {activeTab === 'roi-dashboard' && <ROIDashboard />}

      {/* Risk Settings Tab */}
      {activeTab === 'risk-settings' && (
        <div className="space-y-6">
          {/* Sub-tabs for Risk Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setRiskSettingsSubTab('process-metadata')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'process-metadata'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Process Metadata</span>
              </button>
              <button
                onClick={() => setRiskSettingsSubTab('definitions-acronyms')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'definitions-acronyms'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Definitions & Acronyms</span>
              </button>
              <button
                onClick={() => setRiskSettingsSubTab('risk-appetite')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'risk-appetite'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Target className="w-4 h-4" />
                <span>Risk Appetite Config</span>
              </button>
              <button
                onClick={() => setRiskSettingsSubTab('risk-taxonomy')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'risk-taxonomy'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List className="w-4 h-4" />
                <span>Risk Taxonomy</span>
              </button>
              <button
                onClick={() => setRiskSettingsSubTab('regulatory-frameworks')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                  riskSettingsSubTab === 'regulatory-frameworks'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                <span>Regulatory Frameworks</span>
              </button>
            </div>
          </div>

          {riskSettingsSubTab === 'process-metadata' && <ProcessMetadata />}
          {riskSettingsSubTab === 'definitions-acronyms' && (
            <div className="space-y-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-purple-900 mb-2">
                  Terminology Templates
                </h3>
                <p className="text-purple-700 text-sm">
                  Upload terminology templates (PDF, Word, Excel) to automatically extract definitions for terminology matching during document analysis.
                  These definitions will be available across all projects in your organization.
                </p>
              </div>

              {currentProject ? (
                <TerminologyTemplateUpload projectId={currentProject.id} />
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">
                    Please select a project from the Dashboard to upload terminology templates.
                  </p>
                </div>
              )}
            </div>
          )}
          {riskSettingsSubTab === 'risk-appetite' && <RiskAppetiteConfig />}
          {riskSettingsSubTab === 'risk-taxonomy' && <RiskTaxonomyManagement />}
          {riskSettingsSubTab === 'regulatory-frameworks' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-blue-900">{frameworkStats.totalStandards}</div>
                      <div className="text-blue-700 text-sm">Total Standards</div>
                    </div>
                    <BookOpen className="h-8 w-8 text-blue-400" />
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-purple-900">{frameworkStats.frameworkTypes}</div>
                      <div className="text-purple-700 text-sm">Framework Types</div>
                    </div>
                    <Shield className="h-8 w-8 text-purple-400" />
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-green-900">4</div>
                      <div className="text-green-700 text-sm">Industries Covered</div>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-400" />
                  </div>
                </div>
              </div>

              {/* Info Banner with Export */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-blue-900 mb-1">About Regulatory Frameworks</h3>
                  <p className="text-sm text-blue-800">
                    This section provides access to major regulatory frameworks including Basel Committee principles,
                    NIST Cybersecurity Framework, ISO 27002 controls, and COSO Internal Control standards.
                    Click on any standard to view detailed requirements and related controls.
                  </p>
                </div>
                <button
                  onClick={handleExportFrameworks}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>
              </div>

              {/* Error Display */}
              {frameworkError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-800 mb-1">Error Loading Frameworks</h3>
                    <p className="text-sm text-red-700">{frameworkError}</p>
                    <button
                      onClick={loadFrameworks}
                      className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              {/* Framework List */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <FrameworkList
                  frameworks={frameworks}
                  onSelectFramework={handleSelectFramework}
                  loading={frameworkLoading}
                />
              </div>

              {/* Framework Categories Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Basel Committee</h4>
                  <p className="text-sm text-blue-800">
                    11 principles for operational risk management in banking institutions
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">NIST Frameworks</h4>
                  <p className="text-sm text-purple-800">
                    Cybersecurity Framework 2.0 and SP 800-53 security controls
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">ISO 27002</h4>
                  <p className="text-sm text-green-800">
                    Information security controls across organizational, technical, and physical domains
                  </p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-900 mb-2">COSO Framework</h4>
                  <p className="text-sm text-orange-800">
                    5 components and 17 principles for internal control and enterprise risk management
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Workflow Tools Tab - Updated with sub-tabs */}
      {activeTab === 'workflow-tools' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="flex space-x-2">
              <button
                onClick={() => setWorkflowToolsSubTab('workflow-automation')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  workflowToolsSubTab === 'workflow-automation'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Workflow Automation Platforms</span>
              </button>
              <button
                onClick={() => setWorkflowToolsSubTab('sop-formats')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  workflowToolsSubTab === 'sop-formats'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>SOP Formats</span>
              </button>
            </div>
          </div>

          {workflowToolsSubTab === 'workflow-automation' && <WorkflowToolsAdmin />}
          {workflowToolsSubTab === 'sop-formats' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-super-dark-purple mb-4">SOP Format Templates</h2>
              <p className="text-gray-600 mb-6">
                Standard Operating Procedure format templates for different types of documents and workflows.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  SOP format management will be available here.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'tenant-management' && currentUser?.role === 'SUPER_ADMIN' && <TenantManagement />}

      {/* User Create Modal */}
      <UserCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onUserCreated={() => {
          fetchUsers();
          fetchStats();
        }}
      />

      {/* Framework Detail Modal */}
      <FrameworkDetailModal
        framework={selectedFramework}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedFramework(null);
        }}
        onLoadControls={handleLoadControls}
        onLoadCitations={handleLoadCitations}
      />

    </div>
  );
};

export default Admin;
